/// <reference lib="webworker" />
// export default null;
declare let self: ServiceWorkerGlobalScope;
import { cleanupOutdatedCaches } from 'workbox-precaching';
import { clientsClaim } from 'workbox-core';

import { registerRoute, Route, NavigationRoute } from 'workbox-routing';
import { NetworkFirst, CacheFirst } from 'workbox-strategies';
import { ExpirationPlugin } from 'workbox-expiration';

import {
	SERVICE_WORKER_CHANNEL_NAME,
	GET_STYLES_QUERY,
	GET_ISSUE_BY_ID_QUERY,
	mapIssuesNonImplementedFeatures,
} from './constants';
import { StyleBuilder } from './components/article/StyleBuilder';
import config from './config';

cleanupOutdatedCaches();

// Delay requests for 15 seconds
const DELAY = 5 * 1000;

// Give TypeScript the correct global.
declare type ExtendableEvent = any;

swVersion();

const CACHE_PREFIX = 'cf';
const CACHE_IMAGES_NAME = `${CACHE_PREFIX}-images`;
const CACHE_AUDIOS_NAME = `${CACHE_PREFIX}-audios`;
const CACHE_VIDEOS_NAME = `${CACHE_PREFIX}-videos`;
const CACHE_FONTS_NAME = `${CACHE_PREFIX}-fonts`;
const CACHE_ASSETS_NAME = `${CACHE_PREFIX}-assets`;

const channel = new BroadcastChannel(SERVICE_WORKER_CHANNEL_NAME);

const imagesExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const audiosExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const videosExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const fontsExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const assetsExpirationPlugin = new ExpirationPlugin({
	purgeOnQuotaError: true,
	maxAgeSeconds: 30 * 24 * 60 * 60, // 30 Days
});

const cacheName = 'canvasflow'; //cacheNames.runtime;

const registerCacheFirstRouteUsing = (
	destination: RequestDestination,
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		({ request }) => request.destination === destination,
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

const registerCacheFirstRouteUsingExtension = (
	extensions: Array<string>,
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		({ request }) => {
			extensions.join('|');
			const reg = new RegExp(`.*\\.(?:${extensions.join('|')})$`);
			return reg.test(request.url);
		},
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

const registerWebAssetsImagesCacheFirstRouteUsing = (
	cacheName: string,
	expirationPlugin: ExpirationPlugin,
): Route =>
	registerRoute(
		(data) => {
			const { request, url } = data;
			if (
				/^https:\/\/www\.google/.test(url.origin) ||
				/^https:\/\/analytics\.google/.test(url.origin)
			) {
				return false;
			}
			return /.*\.(?:png|webp|jpg|jpeg|gif)$/.test(request.url);
		},
		new CacheFirst({
			cacheName: cacheName,
			plugins: [expirationPlugin],
		}),
	);

registerWebAssetsImagesCacheFirstRouteUsing(
	CACHE_IMAGES_NAME,
	imagesExpirationPlugin,
);
if (!isSafari()) {
	registerRoute(new NavigationRoute(new NetworkFirst({ cacheName })));
}

registerCacheFirstRouteUsingExtension(
	['m3u8', 'ts', 'mp3'],
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);

registerCacheFirstRouteUsing(
	'audio',
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);
registerCacheFirstRouteUsing(
	'track',
	CACHE_AUDIOS_NAME,
	audiosExpirationPlugin,
);
registerCacheFirstRouteUsing(
	'video',
	CACHE_VIDEOS_NAME,
	videosExpirationPlugin,
);

registerCacheFirstRouteUsing('font', CACHE_FONTS_NAME, fontsExpirationPlugin);

registerCacheFirstRouteUsing(
	'style',
	CACHE_ASSETS_NAME,
	assetsExpirationPlugin,
);

self.addEventListener('install', (event: ExtendableEvent) => {
	info(`SERVICE WORKER INSTALLATION: ${config.build.version}`);
	event.waitUntil(
		caches
			.open(cacheName)
			.then((cache) => {
				return cache.addAll(['/']);
				// return cache.addAll(cacheEntries);
			})
			.then(() => self.skipWaiting())
			.catch(console.error),
	);
});

self.addEventListener('activate', (event: ExtendableEvent) => {
	info(`ACTIVATE SERVICE WORKER: ${config.build.version}`);
	event.waitUntil(self.clients.claim());
});

if (!isSafari()) {
	info('The browser is not safari so i overwrite fetch');
} else {
	info('Is safari so i will ignore fetch');
}

self.addEventListener('message', (event: ExtendableEvent) => {
	const { type, payload } = event.data;
	const messageType = new Set([
		'DOWNLOAD_IMAGES',
		'DOWNLOAD_BACKGROUND_IMAGES',
		'DOWNLOAD_BACKGROUND_REPLICA_PAGES',
		'DOWNLOAD_BACKGROUND_ISSUE',
		'DOWNLOAD_BACKGROUND_VIDEOS',
		'DOWNLOAD_BACKGROUND_STYLES',
		'VERSION',
	]);

	if (!messageType.has(type)) {
		return;
	}

	info(`RECEIVE MESSAGE: "${type}"`);

	switch (type) {
		case 'DOWNLOAD_IMAGES':
			onDownloadImages(payload, event);
			break;
		case 'VERSION':
			swVersion();
			break;
		case 'DOWNLOAD_BACKGROUND_IMAGES':
			onDownloadBackgroundImage(payload);
			break;
		case 'DOWNLOAD_BACKGROUND_REPLICA_PAGES':
			onDownloadBackgroundPages(payload);
			break;
		case 'DOWNLOAD_BACKGROUND_ISSUE':
			onDownloadIssue(payload);
			break;
		case 'DOWNLOAD_BACKGROUND_VIDEOS':
			onDownloadVideos(payload);
			break;
		case 'DOWNLOAD_BACKGROUND_STYLES':
			onDownloadStyles();
			break;
		default:
			return;
	}
});

function onDownloadStyles() {
	setTimeout(() => {
		log(`DOWNLOAD STYLES IN THE BACKGROUND`);
		fetchStyles()
			.then((styles) => {
				channel.postMessage({
					type: 'DOWNLOAD_STYLES_MESSAGE',
					payload: {
						styles,
					},
				});
			})
			.catch(console.error);
	}, DELAY);
}

async function fetchStyles() {
	const url = `${config.build.host}/graphql`;
	const response = await fetch(url, {
		method: 'POST',
		mode: 'cors',
		cache: 'no-cache',
		headers: {
			'Content-Type': 'application/json',
			'app-key': config.build.appKey,
		},
		body: JSON.stringify({
			query: GET_STYLES_QUERY,
		}),
	});
	const result = await response.json();
	return buildStyles(result.data.styles);
}

function onDownloadIssue(payload: any) {
	setTimeout(() => {
		log(`DOWNLOAD ISSUE IN THE BACKGROUND`, payload);
		const { id, token } = payload;
		fetchIssueById(id, token)
			.then((issue) => {
				log(`ISSUE IS DOWNLOADED IN THE BACKGROUND`, issue);
				if (!issue) {
					return;
				}
				channel.postMessage({
					type: 'DOWNLOAD_ISSUE_MESSAGE',
					payload: {
						issue,
					},
				});
			})
			.catch(console.error);
	}, DELAY);
}

async function fetchIssueById(id: string, token: string) {
	const url = `${config.build.host}/graphql`;
	const body = {
		operationName: 'Issue',
		variables: {
			id: id,
		},
		query: GET_ISSUE_BY_ID_QUERY,
	};
	const response = await fetch(url, {
		method: 'POST',
		mode: 'cors',
		cache: 'no-cache',
		headers: {
			'Content-Type': 'application/json',
			'app-key': config.build.appKey,
			Authorization: token,
		},
		body: JSON.stringify(body),
	});
	const result = await response.json();
	let issue: any = result.data.issue;
	if (issue) {
		// TODO Remove to restore features
		issue = mapIssuesNonImplementedFeatures(issue);
	}
	return issue;
}

function onDownloadBackgroundPages(payload: any) {
	const { IssueID } = payload;
	log(`Downloading pages in service worker from the replica: ${IssueID}`);
	const fetchImages = async (images: Array<any>) => {
		for (const image of images) {
			try {
				await fetch(image);
			} catch (e) {
				console.error(e);
			}
		}
	};
	const requestImages = async (images: Array<any>) => {
		const imagesToDownload: Array<string> = [];
		try {
			if (caches) {
				const cache = await caches.open(CACHE_IMAGES_NAME);
				for (const image of images) {
					// If the image do not exist in the cache add it
					if (!(await cache.match(image))) {
						imagesToDownload.push(image);
					}
				}
				info(`Total of pages sent to download: ${images.length}`);
				info(
					`Download this amount of pages: ${imagesToDownload.length}`,
				);
				await cache.addAll(imagesToDownload);
				info(
					`I succeed adding ${imagesToDownload.length} images to the cache`,
				);
			} else {
				info(
					`I failed fetching the ${imagesToDownload.length} images so i will try using fetch`,
				);
				await fetchImages(images);
			}
		} catch (e) {
			console.error(e);
		}
	};

	setTimeout(() => {
		requestImages(payload.pages)
			.then(() => {
				console.info(`Success downloading the pages`);
			})
			.catch(console.error);
	}, DELAY);
}
function buildStyles(styles: Array<any>) {
	styles = styles.map(StyleBuilder.mapRenameProperties).map(clone);
	styles = StyleBuilder.resolveInheritance(styles);
	return styles;
}

function onDownloadBackgroundImage(payload: any) {
	info(`Downloading images in service worker: ${payload.images.length}`);
	const fetchImages = async (images: Array<any>) => {
		for (const image of images) {
			try {
				await fetch(image);
			} catch (e) {
				console.error(e);
			}
		}
	};
	const requestImages = async (images: Array<any>) => {
		const imagesToDownload: Array<string> = [];
		try {
			if (caches) {
				const cache = await caches.open(CACHE_IMAGES_NAME);
				for (const image of images) {
					// If the image do not exist in the cache add it
					if (!(await cache.match(image))) {
						imagesToDownload.push(image);
					}
				}
				info(`Total of images sent to download: ${images.length}`);
				info(
					`Download this amount of images: ${imagesToDownload.length}`,
				);
				await cache.addAll(imagesToDownload);
				info(
					`I succeed adding ${imagesToDownload.length} images to the cache`,
				);
			} else {
				info(
					`I failed fetching the ${imagesToDownload.length} images so i will try using fetch`,
				);
				await fetchImages(images);
			}
		} catch (e) {
			error(e);
		}
	};

	setTimeout(() => {
		requestImages(payload.images)
			.then(() => {
				console.info(`Success downloading the images`);
			})
			.catch(console.error);
	}, DELAY);
}
function onDownloadImages(payload: any, event: any) {
	const { images, IssueID } = payload;
	console.info(`Download images in the service worker`);
	setTimeout(async () => {
		try {
			const clients = await self.clients.matchAll({ type: 'window' });
			console.info(`I start downloading the ${images.length} images`);
			await downloadImages(images);
			console.info(
				`I finish processing all the images (${images.length}) for the issue ${IssueID}`,
			);
			event.ports[0].postMessage({
				type: 'DOWNLOAD_IMAGES',
				payload,
			});
			for (const client of clients) {
				client.postMessage({
					type: 'DOWNLOAD_IMAGES',
					payload,
				});
			}
		} catch (e) {
			event.ports[0].postMessage({
				type: 'ERROR',
				payload: {
					error: e,
				},
			});
		}
	}, 2000);
}
function onDownloadVideos(payload: any) {
	const { videos, IssueID } = payload;
	info(`Download videos in the service worker`);
	setTimeout(async () => {
		try {
			info(`I start downloading the ${videos.length} videos`);
			await downloadVideos(videos);
			info(
				`I finish processing all the videos (${videos.length}) for the issue ${IssueID}`,
			);
		} catch (e) {
			error(e);
		}
	}, 2000);
}

async function downloadImages(images: Array<string>) {
	const clients = await self.clients.matchAll({ type: 'window' });

	for (const image of images) {
		await downloadImage(image);
		for (const client of clients) {
			client.postMessage({
				type: 'DOWNLOAD_IMAGE',
				payload: {
					image,
					cacheName: CACHE_IMAGES_NAME,
				},
			});
		}
	}
}

async function downloadVideos(videos: Array<string>) {
	for (const video of videos) {
		await downloadVideo(video);
	}
}

async function downloadImage(image: string) {
	try {
		if (caches) {
			console.debug(`Download image`);
			console.debug(image);
			const cache = await caches.open(CACHE_IMAGES_NAME);
			await cache.addAll([image]);
		} else {
			await fetch(image);
		}
	} catch (e) {
		console.error(e);
	}
}

async function downloadVideo(video: string) {
	try {
		if (caches) {
			console.debug(`Download image`);
			console.debug(video);
			const cache = await caches.open(CACHE_VIDEOS_NAME);
			await cache.addAll([video]);
		} else {
			await fetch(video);
		}
	} catch (e) {
		console.error(e);
	}
}

// this is necessary, since the new service worker will keep on skipWaiting state
// and then, caches will not be cleared since it is not activated
if (self.skipWaiting) {
	self.skipWaiting();
}

clientsClaim();

function log(msg: any, ...optionalParams: any[]) {
	const color = '#64d1ff';
	console.log('%csw', logStyle(color), msg, ...optionalParams);
}

/*function debug(msg: any) {
	const color = '#0b84ff';
	console.log('%csw', logStyle(color), msg);
}*/

function info(msg: any) {
	const color = '#fed709';
	console.info('%csw', logStyle(color), msg);
}

function error(msg: any) {
	const color = '#ff375f';
	console.info('%csw', logStyle(color), msg);
}

function logStyle(color: string) {
	return `color: white; background: ${color}; border-radius: 6px; padding: 2px 12px;`;
}

function isSafari() {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

function swVersion() {
	info(`SW - Version: ${config.build.version}`);
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
